import {
    alphaAtlasRowEntityKeys,
    ALPHA_ATLAS_DURATIONS,
    ALPHA_ATLAS_OUTCOMES,
    ALPHA_ATLAS_VIEWS,
    type AlphaAtlasDuration,
    type AlphaAtlasExposure,
    type AlphaAtlasFilterState,
    type AlphaAtlasOutcomeKind,
    type AlphaAtlasReportFilters,
    type AlphaAtlasReportRow,
    type AlphaAtlasReportSource,
    buildAlphaAtlasQuery,
    buildAlphaAtlasSearchParams,
    changeAlphaAtlasOutcome,
    countActiveNarrowingFilters,
    DEFAULT_ALPHA_ATLAS_FILTERS,
    isVerificationBearingWindow,
    isAlphaAtlasSliceAvailable,
    parseAlphaAtlasSearchParams,
    reconcileAlphaAtlasFiltersForSlice,
    resolveAlphaAtlasViewState,
    SORT_META,
    WINDOW_ROLE_META
} from './AlphaAtlasReportState';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import UserLayout from '@/components/Layout/UserLayout';
import Button from '@/components/UI/Button';
import Callout from '@/components/UI/Callout';
import Disclosure from '@/components/UI/Disclosure';
import EmptyState from '@/components/UI/EmptyState';
import Pagination from '@/components/UI/Pagination';
import SegmentedControl from '@/components/UI/SegmentedControl';
import { StatList, StatRow } from '@/components/UI/StatRow';
import StatusBadge from '@/components/UI/StatusBadge';
import Tooltip from '@/components/UI/Tooltip';
import AlphaAtlasCombinationLookup from './AlphaAtlasCombinationLookup';
import AlphaAtlasFilters from './AlphaAtlasFilters';
import AlphaAtlasResultDrawer from './AlphaAtlasResultDrawer';
import AlphaAtlasResultsTable from './AlphaAtlasResultsTable';
import AlphaAtlasRevealDialog from './AlphaAtlasRevealDialog';
import { formatCount, formatHorizon, formatMarketDate, formatMarketDateTime, humanizeCatalogLabel } from './alphaAtlasFormat';

const SourceProvenance = ({ source }: { source: AlphaAtlasReportSource }) => (
    <div className="text-xs text-zinc-500">
        <span>
            Publication #{source.publicationId} · {formatCount(source.entityCount)} entities · as of {formatMarketDate(source.asOfAtUnix)} ·
            history from {formatMarketDate(source.firstEligibleSessionAtUnix)}
        </span>
        {source.outcomeKind === 'option' && (
            <p className="mt-1">
                Index option outcomes require an exit before the selected contract’s last trading time. Horizons that reach expiration are
                unavailable.
            </p>
        )}
        <Disclosure title="Boundaries and lineage" variant="plain">
            <div className="max-w-xl pb-1">
                <StatList>
                    <StatRow label="Canonical anchor" value={formatMarketDateTime(source.anchorAtUnix)} />
                    <StatRow label="First eligible session" value={formatMarketDateTime(source.firstEligibleSessionAtUnix)} />
                    <StatRow label="Latest safe session" value={formatMarketDateTime(source.latestSafeSessionAtUnix)} />
                    <StatRow
                        label="Historical boundary (one year before as-of)"
                        value={formatMarketDateTime(source.historicalBoundaryAtUnix)}
                    />
                    <StatRow
                        label="Verification boundary (six months before as-of)"
                        value={formatMarketDateTime(source.verificationBoundaryAtUnix)}
                    />
                    <StatRow
                        label="Generation / dataset / manifest / catalog"
                        value={`${source.generationId} / ${source.datasetId} / ${source.manifestId} / ${source.catalogId}`}
                    />
                    <StatRow label="Window results" value={formatCount(source.windowResultRowCount)} />
                    <StatRow
                        label="Outcomes / memberships"
                        value={`${formatCount(source.outcomeRowCount)} / ${formatCount(source.membershipRowCount)}`}
                    />
                    <StatRow label="Random baselines" value={formatCount(source.randomBaselineRowCount)} />
                </StatList>
            </div>
        </Disclosure>
    </div>
);

/** Window boundaries for the active view, preferring the publication's per-horizon window table over the first row. */
const resolveWindowRange = (
    filters: AlphaAtlasFilterState,
    options: AlphaAtlasReportFilters | undefined,
    firstRow: AlphaAtlasReportRow | undefined
): { start: number | null; end: number | null } | null => {
    const window =
        options?.windows.find(
            (candidate) => candidate.windowRole === filters.windowRole && candidate.horizonSeconds === filters.horizonSeconds
        ) ?? options?.windows.find((candidate) => candidate.windowRole === filters.windowRole);
    if (window) return { start: window.startAtUnix, end: window.endAtUnix };
    if (firstRow) return { start: firstRow.windowStartAtUnix, end: firstRow.windowEndAtUnix };
    return null;
};

const AlphaAtlasReport = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [exposure, setExposure] = useState<AlphaAtlasExposure | null>(null);
    const [selectedRow, setSelectedRow] = useState<AlphaAtlasReportRow | null>(null);
    const [revealTarget, setRevealTarget] = useState<AlphaAtlasReportRow | null>(null);

    const urlFilters = useMemo(() => parseAlphaAtlasSearchParams(searchParams), [searchParams]);
    // Revealed windows need a requester-bound exposure that lives only in this session; without one, fall back to the controlled view.
    const filters = useMemo<AlphaAtlasFilterState>(
        () => ({
            ...urlFilters,
            windowRole: !exposure && isVerificationBearingWindow(urlFilters.windowRole) ? 'training' : urlFilters.windowRole,
            exposureFingerprint: exposure?.exposureFingerprint
        }),
        [urlFilters, exposure]
    );

    const applyFilters = useCallback(
        (next: AlphaAtlasFilterState) => setSearchParams(buildAlphaAtlasSearchParams(next)),
        [setSearchParams]
    );
    const updateFilters = (patch: Partial<AlphaAtlasFilterState>) => applyFilters({ ...filters, ...patch, page: 1 });
    const availableSlices = useQuery({
        ...trpc.alphaAtlas.availableSlices.queryOptions(),
        staleTime: 300_000
    });
    const sliceAvailable = isAlphaAtlasSliceAvailable(availableSlices.data, filters.durationId, filters.outcomeKind);
    const availabilityLoaded = availableSlices.data !== undefined;
    const changeDuration = (durationId: AlphaAtlasDuration) => {
        const targetOutcome = isAlphaAtlasSliceAvailable(availableSlices.data, durationId, filters.outcomeKind)
            ? filters.outcomeKind
            : availableSlices.data?.find((slice) => slice.durationId === durationId && slice.available)?.outcomeKind;
        if (!targetOutcome) return;
        setExposure(null);
        setSelectedRow(null);
        applyFilters({ ...DEFAULT_ALPHA_ATLAS_FILTERS, durationId, outcomeKind: targetOutcome });
    };
    const changeOutcome = (outcomeKind: AlphaAtlasOutcomeKind) => {
        if (!isAlphaAtlasSliceAvailable(availableSlices.data, filters.durationId, outcomeKind)) return;
        setExposure(null);
        setSelectedRow(null);
        setRevealTarget(null);
        applyFilters(changeAlphaAtlasOutcome(filters, outcomeKind));
    };
    const clearNarrowing = () =>
        updateFilters({
            scope: 'pooled',
            entityKeys: [],
            conditionKind: undefined,
            hypothesisFingerprint: undefined,
            parentFingerprint: undefined,
            childFingerprint: undefined,
            evidenceState: undefined,
            minSupport: undefined,
            acrossWindows: 'any',
            bundleEntities: false
        });
    const returnToControlledView = () => {
        setExposure(null);
        updateFilters({
            hypothesisFingerprint: undefined,
            windowRole: isVerificationBearingWindow(filters.windowRole) ? 'training' : filters.windowRole
        });
    };

    const filterOptions = useQuery({
        ...trpc.alphaAtlas.reportFilters.queryOptions({ durationId: filters.durationId, outcomeKind: filters.outcomeKind }),
        enabled: availabilityLoaded && sliceAvailable,
        staleTime: 300_000
    });
    const currentFilterOptions =
        filterOptions.data?.source.durationId === filters.durationId && filterOptions.data.source.outcomeKind === filters.outcomeKind
            ? filterOptions.data
            : undefined;
    const reconciledFilters = currentFilterOptions ? reconcileAlphaAtlasFiltersForSlice(filters, currentFilterOptions) : undefined;
    const filtersMatchSlice =
        reconciledFilters !== undefined &&
        buildAlphaAtlasSearchParams(reconciledFilters).toString() === buildAlphaAtlasSearchParams(filters).toString();
    useEffect(() => {
        if (reconciledFilters && !filtersMatchSlice) applyFilters(reconciledFilters);
    }, [applyFilters, filtersMatchSlice, reconciledFilters]);
    const report = useQuery({
        ...trpc.alphaAtlas.reportResults.queryOptions(buildAlphaAtlasQuery(filters)),
        enabled: availabilityLoaded && sliceAvailable && filtersMatchSlice && filters.view === 'find',
        staleTime: 60_000,
        placeholderData: keepPreviousData
    });
    const reveal = useMutation({
        ...trpc.alphaAtlas.revealFullHistory.mutationOptions(),
        onSuccess: (result) => {
            const target = revealTarget;
            setExposure(result);
            setRevealTarget(null);
            setSelectedRow(null);
            applyFilters({
                ...filters,
                hypothesisFingerprint: result.hypothesisFingerprint,
                horizonSeconds: result.horizonSeconds,
                scope: target?.scope ?? filters.scope,
                entityKeys: target ? alphaAtlasRowEntityKeys(target) : [],
                conditionKind: target?.conditionKind,
                direction: target?.direction,
                windowRole: 'wholeRange',
                page: 1
            });
        }
    });

    const reportDataMatchesSlice =
        report.data?.source.durationId === filters.durationId && report.data.source.outcomeKind === filters.outcomeKind;
    const reportData = reportDataMatchesSlice ? report.data : undefined;
    const rows = reportData?.items ?? [];
    const total = reportData?.total ?? 0;
    const reportError = filterOptions.error ?? report.error;
    const reportLoading =
        !reportError && (filterOptions.isPending || !filtersMatchSlice || report.isPending || (!reportData && report.isFetching));
    const viewState = resolveAlphaAtlasViewState(reportLoading, reportError, rows.length);
    const source = currentFilterOptions?.source ?? reportData?.source;
    const windowRange = resolveWindowRange(filters, currentFilterOptions, rows[0]);
    const narrowingCount = countActiveNarrowingFilters(filters);
    const firstShown = total === 0 ? 0 : (filters.page - 1) * filters.pageSize + 1;
    const lastShown = Math.min(total, filters.page * filters.pageSize);
    const revealedLabel = exposure
        ? (() => {
              const parent = currentFilterOptions?.parents.find((member) => member.fingerprint === filters.parentFingerprint);
              return parent ? humanizeCatalogLabel(parent.label) : `hypothesis ${exposure.hypothesisFingerprint.slice(0, 12)}…`;
          })()
        : null;

    return (
        <UserLayout containerSize="1600px" title="Alpha Atlas - Project 314">
            <div className="space-y-5 py-2">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="max-w-3xl space-y-2">
                        <div className="flex flex-wrap items-center gap-3">
                            <h3 className="text-xl font-semibold text-white">Alpha Atlas</h3>
                            <Tooltip content="Every result is retrospective research on the currently monitored universe (survivor-biased), so nothing here can authorize trading. Use it to find hypotheses worth testing properly.">
                                <StatusBadge status="exploratory" tone="warning">
                                    Exploratory · survivor-biased · not tradable
                                </StatusBadge>
                            </Tooltip>
                        </div>
                        <p className="text-sm text-zinc-400">
                            The Atlas tests three hypothesis types: a trigger alone, a context alone, or one trigger plus one context. It
                            does not yet test pairs of contexts. Compare both controlled windows here; revealing full history is explicit
                            and recorded.
                        </p>
                        {source && <SourceProvenance source={source} />}
                    </div>
                    <div className="flex flex-wrap items-start gap-4 sm:justify-end">
                        <div className="space-y-1">
                            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Duration</div>
                            <SegmentedControl
                                ariaLabel="Alpha Atlas duration"
                                options={ALPHA_ATLAS_DURATIONS.map((option) => ({
                                    ...option,
                                    disabled: !availableSlices.data?.some((slice) => slice.durationId === option.value && slice.available),
                                    title: availableSlices.data?.some((slice) => slice.durationId === option.value && slice.available)
                                        ? undefined
                                        : 'No published Alpha Atlas slice is available for this duration'
                                }))}
                                value={filters.durationId}
                                onChange={changeDuration}
                            />
                        </div>
                        <div className="space-y-1">
                            <div className="text-xs font-medium uppercase tracking-wide text-zinc-500">Outcome</div>
                            <SegmentedControl
                                ariaLabel="Alpha Atlas outcome"
                                options={ALPHA_ATLAS_OUTCOMES.map((option) => ({
                                    ...option,
                                    disabled: !isAlphaAtlasSliceAvailable(availableSlices.data, filters.durationId, option.value),
                                    title: isAlphaAtlasSliceAvailable(availableSlices.data, filters.durationId, option.value)
                                        ? undefined
                                        : 'This Alpha Atlas duration/outcome slice is not published yet'
                                }))}
                                value={filters.outcomeKind}
                                onChange={changeOutcome}
                            />
                        </div>
                    </div>
                </div>

                {availableSlices.error && (
                    <Callout title="Alpha Atlas availability is unavailable" variant="error">
                        {availableSlices.error.message}
                    </Callout>
                )}
                {availabilityLoaded && !sliceAvailable && (
                    <Callout title="This Alpha Atlas slice is not published yet" variant="info">
                        Choose an available duration and outcome above. The unpublished slice is disabled and no report request was sent.
                    </Callout>
                )}

                {exposure && (
                    <Callout
                        title={`Full history revealed for ${revealedLabel} at ${formatHorizon(exposure.horizonSeconds)}`}
                        variant="warning"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <span>
                                Verification is burned for this hypothesis; whole-range and recent-year results are exploratory and cannot
                                be described as untouched validation evidence.
                            </span>
                            <Button size="sm" variant="outline-dark" onClick={returnToControlledView}>
                                Return to controlled view
                            </Button>
                        </div>
                    </Callout>
                )}

                {sliceAvailable && (
                    <SegmentedControl
                        ariaLabel="Alpha Atlas view"
                        options={ALPHA_ATLAS_VIEWS}
                        value={filters.view}
                        onChange={(view) => updateFilters({ view })}
                    />
                )}

                {sliceAvailable && filters.view === 'lookup' ? (
                    <AlphaAtlasCombinationLookup
                        filters={filters}
                        options={currentFilterOptions}
                        optionsLoading={filterOptions.isPending}
                        queryEnabled={filtersMatchSlice}
                        onChange={updateFilters}
                        onSelectRow={setSelectedRow}
                    />
                ) : sliceAvailable ? (
                    <>
                        <AlphaAtlasFilters
                            exposure={exposure}
                            filters={filters}
                            options={currentFilterOptions}
                            optionsLoading={filterOptions.isPending}
                            onChange={updateFilters}
                            onClearNarrowing={clearNarrowing}
                        />

                        {(filterOptions.error || report.error) && (
                            <Callout title="Alpha Atlas is unavailable" variant="error">
                                {(filterOptions.error ?? report.error)?.message}
                            </Callout>
                        )}

                        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-zinc-400">
                            <span>
                                {reportData ? (
                                    <>
                                        <span className="font-medium text-zinc-200">{formatCount(total)}</span>{' '}
                                        {total === 1 ? 'result' : 'results'} · {WINDOW_ROLE_META[filters.windowRole].label} · ranked by{' '}
                                        {SORT_META[filters.sortBy].label.toLowerCase()}
                                    </>
                                ) : (
                                    'Loading results…'
                                )}
                            </span>
                            {windowRange && (
                                <span className="text-xs">
                                    Window {formatMarketDate(windowRange.start)} – {formatMarketDate(windowRange.end)}
                                </span>
                            )}
                        </div>

                        {viewState === 'empty' ? (
                            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40">
                                <EmptyState
                                    action={
                                        <div className="flex flex-wrap justify-center gap-2">
                                            {filters.page > 1 && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-dark"
                                                    onClick={() => applyFilters({ ...filters, page: 1 })}
                                                >
                                                    Back to first page
                                                </Button>
                                            )}
                                            {narrowingCount > 0 && (
                                                <Button size="sm" variant="outline-dark" onClick={clearNarrowing}>
                                                    Clear filters
                                                </Button>
                                            )}
                                            {filters.hideInsufficientSupport && (
                                                <Button
                                                    size="sm"
                                                    variant="outline-dark"
                                                    onClick={() => updateFilters({ hideInsufficientSupport: false })}
                                                >
                                                    Show insufficient support
                                                </Button>
                                            )}
                                        </div>
                                    }
                                    description={
                                        filters.hideInsufficientSupport
                                            ? 'Insufficient-support rows are hidden. Show them, loosen a filter, or pick a different evidence window.'
                                            : 'Loosen a filter or pick a different evidence window.'
                                    }
                                    title="No results match these filters"
                                />
                            </div>
                        ) : (
                            viewState !== 'error' && (
                                <AlphaAtlasResultsTable
                                    isLoading={viewState === 'loading'}
                                    isRefreshing={report.isPlaceholderData || (report.isFetching && !report.isPending)}
                                    outcomeKind={filters.outcomeKind}
                                    rows={rows}
                                    selectedRowFingerprint={selectedRow?.rowFingerprint ?? null}
                                    sortBy={filters.sortBy}
                                    windowRole={filters.windowRole}
                                    onSelectRow={setSelectedRow}
                                    onSortChange={(sortBy) => updateFilters({ sortBy })}
                                />
                            )
                        )}

                        {total > filters.pageSize && (
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <span className="text-xs text-zinc-500">
                                    Showing {formatCount(firstShown)}–{formatCount(lastShown)} of {formatCount(total)}
                                </span>
                                <Pagination
                                    currentPage={filters.page}
                                    disabled={report.isFetching}
                                    itemsPerPage={filters.pageSize}
                                    totalItems={total}
                                    onPageChange={(page) => applyFilters({ ...filters, page })}
                                />
                            </div>
                        )}
                    </>
                ) : null}
            </div>

            <AlphaAtlasResultDrawer
                durationId={filters.durationId}
                outcomeKind={filters.outcomeKind}
                bundleEntityKeys={selectedRow?.aggregation === 'bundle' ? filters.entityKeys : undefined}
                exposureFingerprint={exposure?.exposureFingerprint}
                isOpen={selectedRow !== null}
                row={selectedRow}
                windowRole={selectedRow?.windowRole ?? filters.windowRole}
                onClose={() => setSelectedRow(null)}
                onSelectRow={setSelectedRow}
                onLookup={(row) => {
                    setSelectedRow(null);
                    updateFilters({
                        view: 'lookup',
                        parentFingerprint: row.parentFingerprint ?? undefined,
                        childFingerprint: row.childFingerprint ?? undefined,
                        direction: row.direction,
                        scope: row.scope,
                        entityKeys: alphaAtlasRowEntityKeys(row),
                        bundleEntities: false
                    });
                }}
                onReveal={(row) => {
                    reveal.reset();
                    setRevealTarget(row);
                }}
            />
            <AlphaAtlasRevealDialog
                error={reveal.error?.message ?? null}
                isPending={reveal.isPending}
                target={revealTarget}
                onCancel={() => setRevealTarget(null)}
                onConfirm={() => {
                    if (!revealTarget) throw new Error('Alpha Atlas reveal confirmed without a target row');
                    reveal.mutate({
                        durationId: filters.durationId,
                        outcomeKind: filters.outcomeKind,
                        hypothesisFingerprint: revealTarget.hypothesisFingerprint,
                        horizonSeconds: revealTarget.horizonSeconds,
                        requesterKey: crypto.randomUUID()
                    });
                }}
            />
        </UserLayout>
    );
};

export default AlphaAtlasReport;
